import { Lightning, Log, Registry } from '@lightningjs/sdk';
import { ToastTranslationKeys } from '../api/models/TranslationKeys';
import { Backend } from '../api/Backend';
import { SysCalls } from './Syscalls';

function offlineEventFunction() {
  SysCalls.onOffline();
}

function onlineEventFunction() {
  SysCalls.onOnline();
}

export abstract class BaseSysCalls {
  protected static appElement: Lightning.Component;
  protected static backend: Backend;
  private static offlineTimer?: number;
  private static triggeredOfflineError: boolean;
  private static offlineListeners: Array<() => void>;
  private static onlineListeners: Array<() => void>;
  private static windowVars: string[];
  public static isOnline: boolean;
  protected static _resumeVideo: boolean;

  static onAppStart(appElement: Lightning.Component, backend: Backend) {
    this.appElement = appElement;
    this.backend = backend;
    this.recordWindow();
    this.isOnline = true;
  }

  static recordWindow() {
    this.windowVars = Object.keys(window).filter((key: string) => window[key as never] != null);
  }

  static init() {
    this._addNetworkListeners();
    Log.warn('[Syscalls]', 'init not implemented');
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function,@typescript-eslint/no-unused-vars
  static exitApp(element: Lightning.Component) {}

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static setScreensaver(bool: boolean) {
    Log.warn('[Syscalls]', 'setScreenSaver not implemented');
  }

  static platformVersion() {
    Log.warn('[Syscalls]', 'platformVersion not implemented');
    return '';
  }

  static modelName() {
    Log.warn('[Syscalls]', 'modelName not implemented');
  }

  static _addNetworkListeners() {
    window.addEventListener('offline', offlineEventFunction);
    window.addEventListener('online', onlineEventFunction);
  }

  static _removeNetworkListeners() {
    window.removeEventListener('offline', offlineEventFunction);
    window.removeEventListener('online', onlineEventFunction);
  }

  //-----
  // shared implementation

  static onOnline() {
    this.isOnline = true;
    if (this.offlineTimer) {
      Registry.clearTimeout(this.offlineTimer);
      this.offlineTimer = undefined;
    } else {
      if (this.triggeredOfflineError) {
        if (this.onlineListeners) this.onlineListeners.forEach(cb => cb());
        this.appElement.fireAncestors(
          '$hideToast',
          this.backend.translations.forKey(ToastTranslationKeys.toast_lost_internet_connection)
        );
        this.triggeredOfflineError = false;
      }
    }
  }

  static onOffline() {
    this.isOnline = false;
    this.offlineTimer = Registry.setTimeout(() => {
      this.offlineTimer = undefined;
      if (this.offlineListeners) this.offlineListeners.forEach(cb => cb());
      this.triggeredOfflineError = true;
      this.appElement.fireAncestors(
        '$showToast',
        this.backend.translations.forKey(ToastTranslationKeys.toast_lost_internet_connection),
        0
      );
    }, 0);
  }

  static addOfflineListener(cb: () => void) {
    if (!this.offlineListeners) this.offlineListeners = [];
    this.offlineListeners.push(cb);
  }

  static removeOfflineListener(cb: () => void) {
    if (!this.offlineListeners) this.offlineListeners = [];
    this.offlineListeners = this.offlineListeners.filter(l => l !== cb);
  }

  static addOnlineListener(cb: () => void) {
    if (!this.onlineListeners) this.onlineListeners = [];
    this.onlineListeners.push(cb);
  }

  static removeOnlineListener(cb: () => void) {
    if (!this.onlineListeners) this.onlineListeners = [];
    this.onlineListeners = this.onlineListeners.filter(l => l !== cb);
  }

  static cleanup(backend: Backend) {
    backend.playerController?.playerService?.cleanup();
    this._removeNetworkListeners();
    if (this.windowVars) {
      const keysToClean = Object.keys(window).filter(key => {
        return window[key as never] != null && !this.windowVars.includes(key);
      });
      keysToClean.forEach(key => {
        delete window[key as never];
      });
    }
  }

  static addOnlineOfflineVideoHandlers() {
    this.addOfflineListener(() => {
      if (!this._resumeVideo) {
        this._resumeVideo = this.backend.playerController.isStreaming;
      }
    });

    this.addOnlineListener(() => {
      // Prevent video from being resumed if app is still in background
      if (this._resumeVideo && !document.hidden) {
        Registry.setTimeout(() => {
          this._resumeVideo = false;
          this.backend.playerController.startRecentStream();
        }, 3000);
      }
    });
  }
}
